import { OrderSizeType } from '@/types/enums/order'
import { toOrderSizeType } from '@/utils/instrument-classification'
import {
  convertNumBondsToParValue,
  convertParValueToNumBonds,
  formatQuantity,
  getQuantityStep,
} from '@/utils/presentation/bonds'
import { formatMoney, PLACEHOLDER } from '@/utils/presentation/formatting'

type SizeType = OrderSizeType | null | undefined

// `type` is a bare string because the trading `Amount` it comes from is typed
// that way; `toOrderSizeType` narrows it.
type AmountLike = {
  type?: string | null
  value?: number | null
} | null

// Par is the only unit the UI divides by $1,000; dividing shares or notional
// would understate the size 1000x.
export function isParSized(sizeType: SizeType): boolean {
  return toOrderSizeType(sizeType) === OrderSizeType.Par
}

export function isNotionalSized(sizeType: SizeType): boolean {
  return toOrderSizeType(sizeType) === OrderSizeType.Notional
}

// Notional formats as money: a bare "5,000" beside bond and share counts reads
// as 5,000 units.
function formatForSizeType(
  quantity: number | null | undefined,
  sizeType: OrderSizeType,
): string {
  if (sizeType === OrderSizeType.Notional) return formatMoney(quantity ?? null)
  return formatQuantity(quantity, {
    isAlreadyInNumBonds: sizeType !== OrderSizeType.Par,
  })
}

const UNIT_LABELS: Record<OrderSizeType, string> = {
  [OrderSizeType.Par]: 'Bonds',
  [OrderSizeType.Shares]: 'Shares',
  [OrderSizeType.Notional]: 'Notional',
}

export function toDisplayQuantity(
  quantity: number,
  sizeType: SizeType,
): number {
  return isParSized(sizeType) ? convertParValueToNumBonds(quantity) : quantity
}

/** Inverse of {@link toDisplayQuantity}. */
export function fromDisplayQuantity(
  displayQuantity: number,
  sizeType: SizeType,
): number {
  return isParSized(sizeType)
    ? convertNumBondsToParValue(displayQuantity)
    : displayQuantity
}

export function formatSizedQuantity(
  quantity: number | null | undefined,
  sizeType: SizeType,
): string {
  return formatForSizeType(quantity, toOrderSizeType(sizeType))
}

/**
 * Increment for the editable quantity inputs, in displayed units. Only par
 * sizing is denominated: shares and notional step by one.
 */
export function getDisplayQuantityStep(
  sizeType: SizeType,
  parValuePerBond?: number | null,
): number {
  return isParSized(sizeType) ? getQuantityStep(parValuePerBond) : 1
}

export function getSizeTypeUnitLabel(sizeType: SizeType): string {
  return UNIT_LABELS[toOrderSizeType(sizeType)]
}

export function getQuantityDisplay(amount: AmountLike | undefined): string {
  if (!amount || typeof amount.value !== 'number') return PLACEHOLDER
  return formatSizedQuantity(amount.value, toOrderSizeType(amount.type))
}

// AG Grid's sort and filter key: the displayed number, so filtering matches.
export function getQuantitySortValue(amount: AmountLike | undefined): number {
  if (!amount || typeof amount.value !== 'number') return 0
  return toDisplayQuantity(amount.value, toOrderSizeType(amount.type))
}

// Empty rather than a label, so an empty quantity cell pairs with an empty type.
export function getQuantityTypeLabel(amount: AmountLike | undefined): string {
  if (!amount || typeof amount.value !== 'number') return ''
  return getSizeTypeUnitLabel(toOrderSizeType(amount.type))
}
